import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { chargesBreakdown } from "./charges";

/* ===== P&L Calc — manual multi-position what-if calculator =====
   Enter positions you hold (long option premium), set target and stop, see
   charges-aware net P&L at target / stop / current, per position and combined.
   Optional: paste a tradingsymbol to auto-fill the current price live.
   Positions persist in this browser (localStorage). Pure calculator — it
   never places orders and is unrelated to the monitor's trade book. */

const LS_KEY = "pnlCalcPositions";
const LOTS = [["NIFTY", 65], ["BN", 30], ["SX", 20]];
const rup = (v) => `${v >= 0 ? "+" : "-"}₹${Math.abs(Math.round(v)).toLocaleString("en-IN")}`;
const money = (v) => `₹${Math.round(v).toLocaleString("en-IN")}`;
const num = (v) => { const n = Number(v); return isFinite(n) ? n : null; };

function newRow() {
  return { id: Date.now() + Math.random(), label: "", optType: "CE", entry: "", qty: "", target: "", stop: "", symbol: "", current: "" };
}

/* net P&L exiting the full qty at price px, charges included */
function netAt(entry, qty, px) {
  if (px == null) return null;
  const gross = (px - entry) * qty;
  const b = chargesBreakdown({ entryPremium: entry, exitPremium: Math.max(px, 0.05), qty });
  return { gross, charges: b ? b.total : 0, net: gross - (b ? b.total : 0) };
}

/* exit price where net = 0 (entry + charges recovered) — two-pass refine */
function breakevenPx(entry, qty) {
  let px = entry;
  for (let i = 0; i < 3; i++) {
    const b = chargesBreakdown({ entryPremium: entry, exitPremium: px, qty });
    px = entry + (b ? b.total : 0) / qty;
  }
  return px;
}

export default function PnlCalc() {
  const [rows, setRows] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(LS_KEY) || "[]"); return s.length ? s : [newRow()]; } catch { return [newRow()]; }
  });
  const [live, setLive] = useState({}); // symbol -> ltp
  useEffect(() => { try { localStorage.setItem(LS_KEY, JSON.stringify(rows)); } catch {} }, [rows]);

  // poll live LTP for rows that have a symbol
  const rowsRef = useRef(rows); rowsRef.current = rows;
  useEffect(() => {
    const tick = async () => {
      const syms = [...new Set(rowsRef.current.map(r => (r.symbol || "").trim().toUpperCase()).filter(Boolean))];
      for (const s of syms) {
        try {
          const res = await axios.get("/api/zerodha/option-ltp", { params: { tradingsymbol: s }, timeout: 4000 });
          const ltp = res.data?.ltp ?? res.data?.data?.ltp;
          if (ltp) setLive(prev => ({ ...prev, [s]: ltp }));
        } catch { /* logged out / bad symbol — leave blank */ }
      }
    };
    tick();
    const id = setInterval(tick, 5000);
    return () => clearInterval(id);
  }, []);

  const upd = (id, key, val) => setRows(rs => rs.map(r => (r.id === id ? { ...r, [key]: val } : r)));
  const addLot = (id, lot) => setRows(rs => rs.map(r => (r.id === id ? { ...r, qty: String((num(r.qty) || 0) + lot) } : r)));

  const calc = useMemo(() => rows.map(r => {
    const entry = num(r.entry), qty = num(r.qty), target = num(r.target), stop = num(r.stop);
    const sym = (r.symbol || "").trim().toUpperCase();
    const current = num(r.current) != null ? num(r.current) : (sym && live[sym]) || null;
    if (!entry || !qty || entry <= 0 || qty <= 0) return { r, ready: false, current };
    return {
      r, ready: true, current,
      capital: entry * qty,
      atTarget: target != null ? netAt(entry, qty, target) : null,
      atStop: stop != null ? netAt(entry, qty, stop) : null,
      atCurrent: current != null ? netAt(entry, qty, current) : null,
      rr: target != null && stop != null && entry > stop ? (target - entry) / (entry - stop) : null,
      be: breakevenPx(entry, qty),
      currentIsLive: num(r.current) == null && sym && live[sym] != null,
    };
  }), [rows, live]);

  const port = useMemo(() => {
    const ready = calc.filter(c => c.ready);
    return {
      n: ready.length,
      capital: ready.reduce((a, c) => a + c.capital, 0),
      best: ready.reduce((a, c) => a + (c.atTarget ? c.atTarget.net : 0), 0),
      worst: ready.reduce((a, c) => a + (c.atStop ? c.atStop.net : 0), 0),
      mtmRows: ready.filter(c => c.atCurrent),
      mtm: ready.reduce((a, c) => a + (c.atCurrent ? c.atCurrent.net : 0), 0),
    };
  }, [calc]);

  const inp = (r, key, ph, width = 84, type = "number") => (
    <input className="ema-select" type={type} placeholder={ph} value={r[key]}
      onChange={e => upd(r.id, key, e.target.value)} style={{ width, minWidth: width }} />
  );

  return (
    <div>
      <div className="dashboard-header">
        <div>
          <h2>P&L Calculator</h2>
          <p className="dashboard-subtitle">Manual what-if for positions you hold — charges-aware net at target / stop / current. Not connected to the trade book; nothing is ever ordered.</p>
        </div>
      </div>

      {/* portfolio strip */}
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12.5 }}>
        <span>{port.n} position{port.n === 1 ? "" : "s"}</span>
        <span>Capital: <b>{money(port.capital)}</b></span>
        <span>Best case (all targets): <b className="positive">{rup(port.best)}</b></span>
        <span>Worst case (all stops): <b className="negative">{rup(port.worst)}</b></span>
        {port.mtmRows.length > 0 && <span>Current MTM ({port.mtmRows.length}): <b className={port.mtm >= 0 ? "positive" : "negative"}>{rup(port.mtm)}</b></span>}
        <span style={{ marginLeft: "auto" }}>
          <button className="btn btn-sm btn-primary" onClick={() => setRows(rs => [...rs, newRow()])}>+ Add position</button>{" "}
          <button className="btn btn-sm" style={{ background: "var(--btn-muted)", color: "var(--grey-light)", border: "1px solid var(--btn-border)" }}
            onClick={() => { if (window.confirm("Clear all positions?")) setRows([newRow()]); }}>Clear all</button>
        </span>
      </div>

      {calc.map(({ r, ready, capital, atTarget, atStop, atCurrent, rr, be, current, currentIsLive }) => (
        <div key={r.id} style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Label<br />{inp(r, "label", "NIFTY 24200", 120, "text")}</label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Type<br />
              <select className="ema-select" value={r.optType} onChange={e => upd(r.id, "optType", e.target.value)} style={{ width: 60 }}>
                <option>CE</option><option>PE</option>
              </select>
            </label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Entry ₹<br />{inp(r, "entry", "15")}</label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Qty<br />{inp(r, "qty", "260")}</label>
            <span style={{ display: "flex", gap: 3, paddingBottom: 2 }}>
              {LOTS.map(([n, l]) => (
                <button key={n} title={`+1 ${n} lot (${l})`} onClick={() => addLot(r.id, l)}
                  style={{ fontSize: 9.5, padding: "3px 6px", background: "var(--btn-muted)", color: "var(--grey-light)", border: "1px solid var(--btn-border)", borderRadius: 4, cursor: "pointer" }}>+{n}</button>
              ))}
            </span>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Target ₹<br />{inp(r, "target", "85")}</label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Stop ₹<br />{inp(r, "stop", "0.50")}</label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Current ₹ (manual)<br />{inp(r, "current", "auto if symbol")}</label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Symbol (optional, live LTP)<br />{inp(r, "symbol", "NIFTY2590924200CE", 170, "text")}</label>
            <span style={{ marginLeft: "auto", display: "flex", gap: 4 }}>
              <button title="Duplicate" onClick={() => setRows(rs => [...rs, { ...r, id: Date.now() + Math.random() }])}
                style={{ fontSize: 11, padding: "3px 8px", background: "var(--btn-muted)", color: "var(--grey-light)", border: "1px solid var(--btn-border)", borderRadius: 4, cursor: "pointer" }}>⧉</button>
              <button title="Remove" onClick={() => setRows(rs => rs.filter(x => x.id !== r.id))}
                className="btn btn-sm btn-danger" style={{ fontSize: 11, padding: "3px 8px" }}>✕</button>
            </span>
          </div>

          {ready ? (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 12.5, alignItems: "center" }}>
              <span style={{ fontWeight: 700, color: "var(--text2)" }}>{r.label || "Position"} {r.optType}</span>
              <span>Capital <b>{money(capital)}</b></span>
              {atTarget && <span>At target: <b className="positive">{rup(atTarget.net)}</b> <span style={{ color: "var(--muted2)", fontSize: 10.5 }}>(gross {rup(atTarget.gross)}, chg ₹{Math.round(atTarget.charges)}, {(atTarget.net / capital * 100).toFixed(0)}%)</span></span>}
              {atStop && <span>At stop: <b className="negative">{rup(atStop.net)}</b> <span style={{ color: "var(--muted2)", fontSize: 10.5 }}>({(atStop.net / capital * 100).toFixed(0)}%)</span></span>}
              {atCurrent && <span>Current{currentIsLive ? " (live)" : ""} @ {current}: <b className={atCurrent.net >= 0 ? "positive" : "negative"}>{rup(atCurrent.net)}</b></span>}
              {rr != null && <span>R:R <b>{rr.toFixed(1)} : 1</b></span>}
              <span style={{ color: "var(--muted2)", fontSize: 10.5 }}>breakeven ≈ ₹{be.toFixed(2)}</span>
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted2)" }}>Enter entry premium and qty to calculate.</div>
          )}
        </div>
      ))}

      <div style={{ fontSize: 10, color: "var(--faint2)", lineHeight: 1.5, marginBottom: 18 }}>
        Long-premium math: net = (exit − entry) × qty − charges (same brokerage/STT/GST formula as the trade book, reconciled to the rupee).
        Positions live only in this browser. Live LTP needs a valid tradingsymbol and an active Kite session.
      </div>

      <CryptoCalc />
    </div>
  );
}

/* ===== Crypto & Gold calculator (Shark perps, INR-margined) =====
   Entry / qty / leverage / exit -> net P&L in INR with the venue's real
   fee model: taker 0.040% + 18% GST per side, funding at 8h stamps. */
const CC_KEY = "pnlCalcCrypto";
const CC_SYMBOLS = ["BTCUSDT", "PAXGUSDT", "ETHUSDT", "SOLUSDT", "BNBUSDT", "XRPUSDT", "BTCINR", "PAXGINR"];
function ccNewRow() {
  return { id: Date.now() + Math.random(), symbol: "BTCUSDT", side: "LONG", entry: "", qty: "", lev: "10", exit: "", days: "0", maker: false };
}
function CryptoCalc() {
  const [rows, setRows] = useState(() => {
    try { const s = JSON.parse(localStorage.getItem(CC_KEY) || "[]"); return s.length ? s : [ccNewRow()]; } catch { return [ccNewRow()]; }
  });
  const [st, setSt] = useState(null);
  useEffect(() => { try { localStorage.setItem(CC_KEY, JSON.stringify(rows)); } catch {} }, [rows]);
  useEffect(() => {
    const load = async () => {
      try { const r = await axios.get("/api/crypto/trader/status", { timeout: 10000 }); if (r.data.success) setSt(r.data); } catch {}
    };
    load(); const id = setInterval(load, 30000); return () => clearInterval(id);
  }, []);
  const upd = (id, k, v) => setRows(rs => rs.map(r => r.id === id ? { ...r, [k]: v } : r));
  const fx = st?.fx?.rate || 95.5;
  const gst = st?.params?.feeGstPct ?? 0.18;
  const takerSide = (st?.params?.takerFeePct ?? 0.0004) * (1 + gst);
  const makerSide = 0.00016 * (1 + gst);
  const fundRate = st?.config?.funding8hPct ?? 0.0001;
  const isU = (s) => String(s).endsWith("USDT");
  const cur = (s) => isU(s) ? "$" : "₹";

  const calc = rows.map(r => {
    const entry = num(r.entry), qty = num(r.qty), lev = Math.max(1, num(r.lev) || 1), exit = num(r.exit), days = Math.max(0, num(r.days) || 0);
    if (!entry || !qty || entry <= 0 || qty <= 0) return { r, ready: false };
    const k = isU(r.symbol) ? fx : 1;
    const feeSide = r.maker ? makerSide : takerSide;
    const short = r.side === "SHORT";
    const notional = entry * qty * k;
    const margin = notional / lev;
    const feeIn = notional * feeSide;
    const live = st?.livePrices?.[r.symbol];
    const at = (xp) => {
      if (xp == null || xp <= 0) return null;
      const gross = (xp - entry) * qty * k * (short ? -1 : 1);
      const feeOut = xp * qty * k * feeSide;
      const funding = days * 3 * fundRate * notional * (short ? 1 : -1); // 3 stamps/day; longs pay
      const net = gross - feeIn - feeOut + funding;
      return { gross, fees: feeIn + feeOut, funding, net, roi: 100 * net / margin };
    };
    // liquidation estimate at chosen leverage (isolated, ~maintenance 15% of margin)
    const liqMove = (1 / lev) * 0.85;
    const liqPx = short ? entry * (1 + liqMove) : entry * (1 - liqMove);
    return { r, ready: true, k, notional, margin, atExit: at(exit), atLive: live ? at(live) : null, live, liqPx };
  });

  return (
    <div>
      <h3 style={{ margin: "6px 0 4px" }}>Crypto &amp; Gold calculator <span style={{ fontSize: 11, color: "var(--muted2)", fontWeight: 400 }}>Shark perps · INR-margined · taker {(takerSide * 100).toFixed(3)}% / maker {(makerSide * 100).toFixed(3)}% per side (incl. GST) · USDT/INR {fx}</span></h3>
      <div style={{ marginBottom: 8 }}>
        <button className="btn btn-sm btn-primary" onClick={() => setRows(rs => [...rs, ccNewRow()])}>+ Add crypto position</button>
      </div>
      {calc.map(({ r, ready, notional, margin, atExit, atLive, live, liqPx }) => (
        <div key={r.id} style={{ background: "var(--chart-bg)", border: "1px solid var(--panel3)", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "flex-end" }}>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Symbol<br />
              <select className="ema-select" value={r.symbol} onChange={e => upd(r.id, "symbol", e.target.value)} style={{ width: 110 }}>
                {CC_SYMBOLS.map(s => <option key={s}>{s}</option>)}
              </select></label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Side<br />
              <select className="ema-select" value={r.side} onChange={e => upd(r.id, "side", e.target.value)} style={{ width: 80 }}>
                <option>LONG</option><option>SHORT</option>
              </select></label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Entry {cur(r.symbol)}<br />
              <input className="ema-select" value={r.entry} onChange={e => upd(r.id, "entry", e.target.value)} placeholder="77000" style={{ width: 100 }} /></label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Qty<br />
              <input className="ema-select" value={r.qty} onChange={e => upd(r.id, "qty", e.target.value)} placeholder="0.05" style={{ width: 90 }} /></label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Leverage<br />
              <input className="ema-select" value={r.lev} onChange={e => upd(r.id, "lev", e.target.value)} placeholder="10" style={{ width: 60 }} /></label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Exit {cur(r.symbol)}<br />
              <input className="ema-select" value={r.exit} onChange={e => upd(r.id, "exit", e.target.value)} placeholder="79000" style={{ width: 100 }} /></label>
            <label style={{ fontSize: 10, color: "var(--muted2)" }}>Days held (funding)<br />
              <input className="ema-select" value={r.days} onChange={e => upd(r.id, "days", e.target.value)} placeholder="0" style={{ width: 60 }} /></label>
            <label style={{ fontSize: 10, color: "var(--muted2)", paddingBottom: 4 }} title="Limit orders pay maker fee 0.016%+GST instead of taker 0.040%+GST">
              <input type="checkbox" checked={!!r.maker} onChange={e => upd(r.id, "maker", e.target.checked)} /> maker (limit orders)
            </label>
            <span style={{ marginLeft: "auto" }}>
              <button title="Remove" onClick={() => setRows(rs => rs.filter(x => x.id !== r.id))} className="btn btn-sm btn-danger" style={{ fontSize: 11, padding: "3px 8px" }}>✕</button>
            </span>
          </div>
          {ready ? (
            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginTop: 10, fontSize: 12.5, alignItems: "center" }}>
              <span style={{ fontWeight: 700 }}>{r.symbol} {r.side === "SHORT" ? "▼ SHORT" : "▲ LONG"}</span>
              <span>Notional <b>{money(notional)}</b></span>
              <span>Margin @ {r.lev}x: <b>{money(margin)}</b></span>
              <span style={{ color: "var(--muted2)", fontSize: 11 }}>est. liq ≈ {cur(r.symbol)}{Math.round(liqPx).toLocaleString()}</span>
              {atExit && <span>At exit: <b className={atExit.net >= 0 ? "positive" : "negative"}>{rup(atExit.net)}</b> <span style={{ color: "var(--muted2)", fontSize: 10.5 }}>(gross {rup(atExit.gross)}, fees ₹{Math.round(atExit.fees)}, funding {rup(atExit.funding)}, ROI {atExit.roi.toFixed(1)}% on margin)</span></span>}
              {atLive && <span>At live {cur(r.symbol)}{live}: <b className={atLive.net >= 0 ? "positive" : "negative"}>{rup(atLive.net)}</b></span>}
            </div>
          ) : (
            <div style={{ marginTop: 8, fontSize: 11, color: "var(--muted2)" }}>Enter entry and qty (exit optional — live price shown when the trader knows the symbol).</div>
          )}
        </div>
      ))}
      <div style={{ fontSize: 10, color: "var(--faint2)", lineHeight: 1.5 }}>
        USDT symbols: prices in $, money converted at the live implied USDT/INR. Fees on NOTIONAL (not margin): taker 0.040% or maker 0.016%, + 18% GST, per side. Funding: 3 stamps/day × {(fundRate * 100).toFixed(3)}% on notional — longs pay, shorts receive (venue rate varies; this uses the trader's config estimate). Liquidation estimate assumes isolated margin, ~15% maintenance. Estimates only.
      </div>
    </div>
  );
}
